import { ApiError, isApiError } from './errors';
import type { ApiErrorCode } from './errors';

/**
 * Maps an ApiError to a SAFE, user-facing message. We NEVER render the raw server
 * `message` for auth flows (it could enable account enumeration or leak internals);
 * instead we key on the machine-readable `code`.
 *
 * `requestId` is surfaced only for codes where a support reference is useful
 * (403 / 5xx / network), rendered as a small mono "Ref:" line by the UI.
 */

export interface DisplayError {
  /** The user-facing sentence to render in the form-level summary. */
  message: string;
  /** Present only for 403/5xx/network — a support correlation id. */
  requestId?: string;
  /** True when the raw error carried no field-level discriminator (form-level). */
  formLevel: boolean;
}

const MESSAGES: Record<ApiErrorCode, string> = {
  unauthorized: 'The email or password is incorrect.',
  too_many_requests: 'Too many attempts. Please wait and try again.',
  forbidden: "You don't have access to this.",
  bad_request: 'Please check your entries and try again.',
  not_found: "We couldn't find what you were looking for.",
  conflict: 'That already exists. Please try a different value.',
  unprocessable_entity: 'Please check your entries and try again.',
  internal_error: 'Something went wrong. Please try again.',
  network_error: 'Something went wrong. Please try again.',
};

/**
 * Form-level copy for a register 409 (duplicate email). Deliberately generic so
 * it never confirms whether an email is already registered (no account
 * enumeration) — the raw server message is never rendered.
 */
export const REGISTER_CONFLICT_MESSAGE =
  'We couldn’t create your account with those details. If you already have an account, sign in instead.';

// Codes for which we show the requestId reference.
const SHOW_REF: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  'forbidden',
  'internal_error',
  'network_error',
]);

export function toDisplayError(err: unknown): DisplayError {
  if (isApiError(err)) {
    const code = err.code;
    const message = MESSAGES[code] ?? MESSAGES.internal_error;
    const hasFields = Array.isArray(err.fields) && err.fields.length > 0;
    return {
      message,
      ...(SHOW_REF.has(code) && err.requestId ? { requestId: err.requestId } : {}),
      formLevel: !hasFields,
    };
  }
  // Unknown/non-API errors degrade to the generic internal message.
  return { message: MESSAGES.internal_error, formLevel: true };
}

/**
 * Extracts field-level messages, but ONLY from a 422 that carries a per-field
 * discriminator. Auth 401s intentionally return no fields (form-level only).
 */
export function toFieldErrors(err: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (isApiError(err) && err.status === 422 && Array.isArray(err.fields)) {
    for (const f of err.fields) {
      out[f.field] = f.message;
    }
  }
  return out;
}

export { ApiError };
